import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  ChevronsLeft,
  ChevronsRight,
  LogOut,
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { useSidebarMenu, SidebarMenuItem } from "../../hooks/useSidebarMenu";
import { useAuth } from "../../store/hooks/useAuth";
import { useEffectivePermissions } from "../../store/hooks/useRbac";
import { RoleTypeEnum } from "../../utils/constants";
import { getPermissionForPath } from "../../utils/rbac/modulePermissions";
import { SimpleTooltip, ConfirmationModal } from "../common";
import { useLayout } from "../../contexts/LayoutContext";
import { getAvatarGradient } from "../../utils/avatarUtils";
import { cn } from "../../utils/cn";

const CrewKnitLogo = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
    <circle cx="6.5" cy="5" r="2" fill="#fff" />
    <circle cx="12" cy="4" r="2" fill="#fff" />
    <circle cx="17.5" cy="5" r="2" fill="#fff" />
    <path d="M4 20c1-6 4.5-9 8-9s7 3 8 9" stroke="#fff" strokeWidth="2.1" strokeLinecap="round" />
    <path d="M9 12.5L16.5 19" stroke="#fff" strokeWidth="2.1" strokeLinecap="round" />
  </svg>
);

const Sidebar: React.FC = () => {
  const { user, logout } = useAuth();
  const { codes, isReady } = useEffectivePermissions();
  const { menuItems } = useSidebarMenu();
  const { pathname } = useLocation();
  const {
    breakpoint,
    sidebarMode,
    isSidebarOpen,
    closeSidebar,
    toggleCollapse,
  } = useLayout();
  const [logoutWarning, setLogoutWarning] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const isSuperAdmin = user?.role?.toLowerCase() === RoleTypeEnum.SUPER_ADMIN?.toLowerCase();

  const canAccessPath = useMemo(() => {
    return (path?: string) => {
      if (!path) return true;
      const permission = getPermissionForPath(path);
      if (!permission) return true;
      const required = Array.isArray(permission) ? permission : [permission];
      return isSuperAdmin || required.some((code) => codes.has(code));
    };
  }, [codes, isSuperAdmin]);

  const getInitials = (name?: string) => {
    if (!name) return "U";
    return name.trim().split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    if (path === "/dashboard") return pathname === path;
    return pathname.startsWith(path);
  };

  const isDrawer = sidebarMode === "drawer" || breakpoint === "mobile" || breakpoint === "tablet";
  const isCollapsed = sidebarMode === "mini" && !isDrawer;
  const isMobile = breakpoint === "mobile";

  const handleNavClick = () => {
    if (isDrawer) closeSidebar();
  };

  // Swipe gesture support for mobile/tablet drawer
  useEffect(() => {
    if (!isDrawer) return;
    const el = sidebarRef.current;
    if (!el) return;

    let startX = 0;
    let startY = 0;
    let isDragging = false;

    const onTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = false;
    };

    const onTouchMove = (e: TouchEvent) => {
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 30) {
        isDragging = true;
      }
    };

    const onTouchEnd = () => {
      if (!isDragging) return;
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", onTouchEnd);

    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [isDrawer, closeSidebar]);

  const renderMenuItem = (item: SidebarMenuItem, index: number) => {
    if (item.type === "group") {
      const permittedChildren = (item.items ?? []).filter(
        (subItem: SidebarMenuItem) => subItem.disabled || canAccessPath(subItem.path),
      );
      if (!permittedChildren.length) return null;

      return (
        <div key={item.code || item.label || index} className="mb-4 first:mt-0 mt-1">
          {!isCollapsed ? (
            <div className="text-[10px] font-extrabold text-sidebar-group-label tracking-widest uppercase px-3 pb-1.5 pt-1 whitespace-nowrap overflow-hidden text-ellipsis select-none">
              {item.label}
            </div>
          ) : (
            <div className="h-px my-2 border-t border-border-secondary mx-2" />
          )}

          <div className="space-y-1">
            {permittedChildren.map((subItem: SidebarMenuItem) => {
              const IconComponent = subItem.icon as React.ElementType;
              if (subItem.disabled) {
                return (
                  <div
                    key={subItem.code || subItem.label}
                    className={cn(
                      "flex items-center gap-2.5 h-[38px] px-3 rounded-[10px] text-text-disabled cursor-not-allowed",
                      isCollapsed && "justify-center px-0"
                    )}
                  >
                    {IconComponent && <IconComponent className="w-[17px] h-[17px] flex-shrink-0" />}
                    {!isCollapsed && (
                      <span className="flex-1 text-left text-[13px] leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
                        {subItem.label}
                      </span>
                    )}
                  </div>
                );
              }

              const navLink = (
                <NavLink
                  key={subItem.path || subItem.code || subItem.label}
                  to={subItem.path || "#"}
                  onClick={handleNavClick}
                  className={({ isActive: navActive }) =>
                    cn(
                      "group relative flex items-center gap-2.5 h-[38px] px-3 rounded-[10px] transition-all duration-150 text-[13px] leading-tight no-underline font-sans",
                      isCollapsed && "justify-center px-0",
                      navActive
                        ? "bg-sidebar-nav-active-bg text-sidebar-text-active font-semibold shadow-nav-active"
                        : "text-sidebar-text font-medium hover:bg-sidebar-nav-hover hover:text-sidebar-text-active"
                    )
                  }
                >
                  {({ isActive: navActive }) => (
                    <>
                      {IconComponent && (
                        <IconComponent
                          className={cn(
                            "w-[17px] h-[17px] flex-shrink-0 transition-colors",
                            navActive
                              ? "text-sidebar-icon-active"
                              : "text-sidebar-icon group-hover:text-sidebar-icon-active"
                          )}
                        />
                      )}
                      {!isCollapsed && (
                        <>
                          <span className="flex-1 text-left whitespace-nowrap overflow-hidden text-ellipsis">
                            {subItem.label}
                          </span>
                          {subItem.badge !== undefined && subItem.badge !== null && (
                            <span className="flex-none px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-error text-white leading-none">
                              {subItem.badge}
                            </span>
                          )}
                        </>
                      )}
                    </>
                  )}
                </NavLink>
              );

              return isCollapsed ? (
                <SimpleTooltip key={subItem.path || subItem.code || subItem.label} label={subItem.label} side="right" className="w-full">
                  {navLink}
                </SimpleTooltip>
              ) : (
                navLink
              );
            })}
          </div>
        </div>
      );
    }

    if (item.disabled) {
      const IconComponent = item.icon as React.ElementType;
      const disabledEl = (
        <div
          key={item.code || item.label || index}
          className={cn(
            "flex items-center gap-2.5 h-[38px] px-3 rounded-[10px] text-text-disabled cursor-not-allowed mb-1",
            isCollapsed && "justify-center px-0"
          )}
        >
          {IconComponent && <IconComponent className="w-[17px] h-[17px] flex-shrink-0" />}
          {!isCollapsed && (
            <span className="flex-1 text-left text-[13px] leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
              {item.label}
            </span>
          )}
        </div>
      );

      return isCollapsed ? (
        <SimpleTooltip key={item.code || item.label || index} label={`${item.label} (Disabled)`} side="right" className="w-full">
          {disabledEl}
        </SimpleTooltip>
      ) : (
        disabledEl
      );
    }

    if (item.path && !canAccessPath(item.path)) return null;

    const IconComponent = item.icon as React.ElementType;

    const mainNavLink = (
      <NavLink
        to={item.path || "#"}
        onClick={handleNavClick}
        className={({ isActive: navActive }) =>
          cn(
            "group relative flex items-center gap-2.5 h-[38px] px-3 rounded-[10px] transition-all duration-150 text-[13px] leading-tight no-underline font-sans",
            isCollapsed && "justify-center px-0",
            navActive
              ? "bg-sidebar-nav-active-bg text-sidebar-text-active font-semibold shadow-nav-active"
              : "text-sidebar-text font-medium hover:bg-sidebar-nav-hover hover:text-sidebar-text-active"
          )
        }
      >
        {({ isActive: navActive }) => (
          <>
            {IconComponent && (
              <IconComponent
                className={cn(
                  "w-[17px] h-[17px] flex-shrink-0 transition-colors",
                  navActive
                    ? "text-sidebar-icon-active"
                    : "text-sidebar-icon group-hover:text-sidebar-icon-active"
                )}
              />
            )}
            {!isCollapsed && (
              <>
                <span className="flex-1 text-left whitespace-nowrap overflow-hidden text-ellipsis">
                  {item.label}
                </span>
                {item.badge !== undefined && item.badge !== null && (
                  <span className="flex-none px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-error text-white leading-none">
                    {item.badge}
                  </span>
                )}
              </>
            )}
          </>
        )}
      </NavLink>
    );

    return (
      <div key={item.path || item.code || item.label || index} className="mb-1">
        {isCollapsed ? (
          <SimpleTooltip label={item.label} side="right" className="w-full">
            {mainNavLink}
          </SimpleTooltip>
        ) : (
          mainNavLink
        )}
      </div>
    );
  };

  if (!isReady) {
    return (
      <>
        {isSidebarOpen && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden" onClick={closeSidebar} />
        )}
      </>
    );
  }

  const sidebarWidth = isDrawer ? "w-[260px]" : (sidebarMode === "mini" ? "w-[76px]" : "w-[248px]");

  return (
    <>
      {/* Backdrop for mobile/tablet drawer */}
      {isDrawer && isSidebarOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden" onClick={closeSidebar} />
      )}

      <aside
        ref={sidebarRef}
        className={`fixed lg:static inset-y-0 left-0 z-50 bg-white border-r border-border-secondary flex flex-col overflow-hidden transition-all duration-200 ease-in-out ${isDrawer
            ? isSidebarOpen ? "translate-x-0" : "-translate-x-full"
            : "lg:translate-x-0"
          } ${sidebarWidth}`}
      >
        {/* Brand Header */}
        <div className="flex items-center gap-[11px] h-[64px] px-[18px] flex-none border-b border-sidebar-border">
          <div className="w-[34px] h-[34px] rounded-[10px] flex-none grid place-items-center bg-gradient-to-br from-primary-700 to-primary-600 shadow-[0_6px_16px_-6px_var(--color-layout-accent-ring)]">
            <CrewKnitLogo />
          </div>
          {(sidebarMode !== "mini" || isDrawer) && (
            <div className="min-w-0">
              <div className="text-brand-title font-extrabold tracking-tight leading-none whitespace-nowrap">
                <span className="text-text-primary">Crew</span>
                <span className="text-primary-700">Knit</span>
              </div>
              <div className="text-brand-sub font-semibold text-text-tertiary tracking-widest mt-[3px] whitespace-nowrap">
                HR SUITE
              </div>
            </div>
          )}
        </div>

        {/* Navigation List */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 pt-3.5 pb-2">
          {menuItems
            .map((item, index) => renderMenuItem(item, index))
            .filter(Boolean) as React.ReactNode[]}
        </nav>

        {/* User Footer */}
        <div className="flex-none border-t border-sidebar-border p-3">
          <div className="flex items-center justify-center gap-2.5 p-2 rounded-[13px] bg-sidebar-user-bg border border-sidebar-user-border">
            <div
              className="w-[38px] h-[38px] rounded-[11px] flex-none grid place-items-center text-white font-bold text-[13px] shadow-xs"
              style={{ background: getAvatarGradient(user?.fullName || "User") }}
            >
              {getInitials(user?.fullName || user?.firstName + " " + user?.lastName)}
            </div>
            {(sidebarMode !== "mini" || isDrawer) && (
              <>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-bold text-text-primary whitespace-nowrap overflow-hidden text-ellipsis">
                    {user?.fullName || "User"}
                  </div>
                  <div className="text-[11px] text-text-tertiary whitespace-nowrap overflow-hidden text-ellipsis">
                    {user?.role || ""}
                  </div>
                </div>
                <SimpleTooltip label="Sign out" side="top">
                  <button
                    onClick={() => setLogoutWarning(true)}
                    className="w-9 h-9 flex items-center justify-center rounded-[10px] bg-error-light text-error hover:bg-error-hover transition-colors cursor-pointer border-none flex-none"
                  >
                    <LogOut className="w-[17px] h-[17px]" />
                  </button>
                </SimpleTooltip>
              </>
            )}
          </div>
          {sidebarMode !== "mini" && !isDrawer && (
            <button
              onClick={toggleCollapse}
              className="mt-2 w-full h-9 flex items-center justify-center gap-2 border-none bg-sidebar-collapse-bg text-text-secondary rounded-[10px] text-body-xs font-semibold cursor-pointer transition-all duration-200 hover:bg-sidebar-collapse-hover hover:text-primary-700"
            >
              <ChevronsLeft className="w-4 h-4" />
              <span>Collapse</span>
            </button>
          )}
          {sidebarMode === "mini" && !isDrawer && (
            <button
              onClick={toggleCollapse}
              className="mt-2 w-full h-9 flex items-center justify-center border-none bg-sidebar-collapse-bg text-text-secondary rounded-[10px] cursor-pointer transition-all duration-200 hover:bg-sidebar-collapse-hover hover:text-primary-700"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </aside>

      <ConfirmationModal
        type="danger"
        title="Logout"
        message="Are you sure you want to logout?"
        onConfirm={() => {
          logout.mutate();
          setLogoutWarning(false);
        }}
        isOpen={logoutWarning}
        onClose={() => setLogoutWarning(false)}
      />
    </>
  );
};

export default Sidebar;
